import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction } from 'react';
import { PropertyOptionsSearchParams } from '@/search/index';
import { Modal, Stack, Text, Select, Button } from '@/theme/components';
import { Divider } from '@/theme/index';
import { DateUtil } from '@/utils/index';
import { useScreenSize } from '@/utils/useScreenSize';

const dateUtil = new DateUtil();

const emptyValues = {
  bathrooms: 0,
  bedrooms: 0,
  property_min_last_sale_date: '',
  property_max_last_sale_date: '',
  property_owner_occupied: false,
  min_price: 0,
  max_price: 0,
};

const minPriceRangeValues = {
  Min: '',
  $50k: '50000',
  $100k: '100000',
  $150k: '150000',
  $200k: '200000',
  $250k: '250000',
  $300k: '300000',
  $350k: '350000',
  $400k: '400000',
  $450k: '450000',
  $500k: '500000',
};

const maxPriceRangeValues = {
  $600k: '600000',
  $700k: '700000',
  $800k: '800000',
  $900k: '900000',
  $1M: '1000000',
  '$1.5M': '1500000',
  $2M: '2000000',
  '$2.5M': '2500000',
  $3M: '3000000',
  $5M: '5000000',
  Max: '',
};

const bedsAndBathsButtonsValues = ['', '1', '2', '3', '4', '5'];

const minPriceRangeOptions = minPriceRangeValues
  ? Object.keys(minPriceRangeValues).map((value) => ({
      display: value,
      value: minPriceRangeValues[value as keyof typeof minPriceRangeValues],
    }))
  : [];

const maxPriceRangeOptions = maxPriceRangeValues
  ? Object.keys(maxPriceRangeValues).map((value) => ({
      display: value,
      value: maxPriceRangeValues[value as keyof typeof maxPriceRangeValues],
    }))
  : [];

export type OwnerSearchModalProps = {
  isModalOpen: boolean;
  closeModal: () => void;
  handleSearch: () => void;
  setSearchValues: Dispatch<SetStateAction<PropertyOptionsSearchParams>>;
  searchValues: PropertyOptionsSearchParams;
};

export const OwnerSearchModal = ({
  isModalOpen,
  closeModal,
  handleSearch,
  setSearchValues,
  searchValues,
}: OwnerSearchModalProps) => {
  const { isMobile } = useScreenSize();

  const handleModalSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSearch();
    closeModal();
  };

  return (
    <Modal
      open={isModalOpen}
      hasCloseIcon
      onClose={closeModal}
      maxWidth={false}
      fullScreen={isMobile}
      closeButtonProps={{
        sx: {
          top: 18,
          right: 18,
          '& > svg': { fontSize: 25, color: 'primary.main' },
        },
      }}
      sx={{
        '.MuiDialog-paper': { width: '100%', maxWidth: '600px' },
      }}
    >
      <form onSubmit={handleModalSubmit}>
        <Text variant="h4">Filters</Text>
        <Divider
          sx={{
            width: '100%',
            marginTop: [1.5, 1.5],
            marginBottom: [1.5, 1.5],
          }}
        />
        <Text>Price (Last Sold Price)</Text>
        <Stack direction="row" spacing={1} marginTop={1.5} marginBottom={1.5}>
          <Select
            options={minPriceRangeOptions}
            onOptionSelect={(value: string) =>
              setSearchValues({ ...searchValues, min_price: Number(value) })
            }
            id="offMarketMinValueSelect"
            value={searchValues.min_price}
          />
          <Select
            options={maxPriceRangeOptions}
            onOptionSelect={(value: string) =>
              setSearchValues({ ...searchValues, max_price: Number(value) })
            }
            id="offMarketMaxValueSelect"
            value={searchValues.max_price}
          />
        </Stack>
        <Text>Last Sold Date</Text>
        <Stack
          direction="row"
          alignItems="center"
          spacing={isMobile ? 1 : 2}
          marginTop={1.5}
          marginBottom={1.5}
        >
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              value={DateTime.fromFormat(
                searchValues.property_min_last_sale_date || '',
                'L/dd/yyyy'
              )}
              onChange={(value) =>
                setSearchValues({
                  ...searchValues,
                  property_min_last_sale_date:
                    dateUtil.parseDateFromString(
                      value?.toString() || '',
                      'L/dd/yyyy'
                    ) || '',
                })
              }
            />
            {!isMobile && <Text>to</Text>}
            <DatePicker
              value={DateTime.fromFormat(
                searchValues.property_max_last_sale_date || '',
                'L/dd/yyyy'
              )}
              onChange={(value) =>
                setSearchValues({
                  ...searchValues,
                  property_max_last_sale_date:
                    dateUtil.parseDateFromString(
                      value?.toString() || '',
                      'L/dd/yyyy'
                    ) || '',
                })
              }
            />
          </LocalizationProvider>
        </Stack>
        <Divider
          sx={{
            width: '100%',
            marginTop: [1.5, 1.5],
            marginBottom: [1.5, 1.5],
          }}
        />
        <Text>Bedrooms</Text>
        <Stack direction="row" spacing={1} marginTop={1.5} marginBottom={1.5}>
          {bedsAndBathsButtonsValues.map((value, index) => (
            <Button
              key={`off-market-bed-${index + 1}`}
              variant={
                String(searchValues.bedrooms || '') === value
                  ? 'contained'
                  : 'outlined'
              }
              color={
                String(searchValues.bedrooms || '') === value
                  ? 'success'
                  : 'primary'
              }
              sx={{ width: isMobile ? '40px' : '100%', minWidth: 'initial' }}
              onClick={() =>
                setSearchValues({ ...searchValues, bedrooms: Number(value) })
              }
            >
              {value === '' ? 'Any' : `${value}+`}
            </Button>
          ))}
        </Stack>
        <Text>Bathrooms</Text>
        <Stack direction="row" spacing={1} marginTop={1.5} marginBottom={1.5}>
          {bedsAndBathsButtonsValues.map((value, index) => (
            <Button
              key={`off-market-bed-${index + 1}`}
              variant={
                String(searchValues.bathrooms || '') === value
                  ? 'contained'
                  : 'outlined'
              }
              color={
                String(searchValues.bathrooms || '') === value
                  ? 'success'
                  : 'primary'
              }
              sx={{ width: isMobile ? '40px' : '100%', minWidth: 'initial' }}
              onClick={() =>
                setSearchValues({ ...searchValues, bathrooms: Number(value) })
              }
            >
              {value === '' ? 'Any' : `${value}+`}
            </Button>
          ))}
        </Stack>
        <Divider
          sx={{
            width: '100%',
            marginTop: [1.5, 1.5],
            marginBottom: [1.5, 1.5],
          }}
        />
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          marginTop={1.5}
          marginBottom={1.5}
        >
          <Button
            onClick={() => setSearchValues({ ...searchValues, ...emptyValues })}
          >
            Reset All
          </Button>
          <Button variant="contained" color="success" type="submit">
            Done
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};
